import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import OverviewTab from "./OverviewTab";
import ChecklistTab from "./ChecklistTab";
import { DateTime } from "components/common/Date";
import { useEnquirySiteVisitOverview } from "./EnquirySiteVisitOverviewContext";
import StatusBadge from "components/common/StatusBadge";
import { EnquirySiteVisitStatusMap } from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import {
  EnquirySiteVisitOverviewTabParam,
  enquirySiteVisitOverviewTabIdFromSearchParam,
  isValidEnquirySiteVisitOverviewTabParam,
  searchParamForEnquirySiteVisitOverviewTabId,
} from "./overviewTabSearchParams";
import EnquirySiteVisitWorkflowActions from "./EnquirySiteVisitWorkflowActions";
import { shouldShowSiteVisitActionButtons } from "features/EnquirySiteVisits/utils/siteVisitActionPolicy";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { enquirySiteVisit } = useEnquirySiteVisitOverview();

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidEnquirySiteVisitOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!enquirySiteVisit) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This site visit could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  const displayTitle = enquirySiteVisit.code || "Site visit";
  const hideActions = !shouldShowSiteVisitActionButtons(enquirySiteVisit.status);
  const locationLine = [
    enquirySiteVisit.address,
    enquirySiteVisit.city,
    enquirySiteVisit.state,
  ]
    .filter(Boolean)
    .join(", ");

  const activeTab = enquirySiteVisitOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForEnquirySiteVisitOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === EnquirySiteVisitOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {locationLine ? (
                        <p className="text-muted mb-1">
                          <i className="ri-map-pin-fill text-danger me-1" />
                          <span className="fw-medium">{locationLine}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={enquirySiteVisit.status}
                            map={EnquirySiteVisitStatusMap}
                          />
                        </div>
                        {enquirySiteVisit.created_at && (
                          <>
                            <div className="vr" />
                            <div>
                              Created:{" "}
                              <span className="fw-medium">
                                <DateTime date={enquirySiteVisit.created_at} />
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-wrap justify-content-md-end">
                      {!hideActions ? (
                        <EnquirySiteVisitWorkflowActions />
                      ) : null}
                      <Link
                        to={`/enquiry-management/site-visits/edit/${enquirySiteVisit.id}`}
                        className="btn btn-light btn-sm border"
                      >
                        <i className="ri-pencil-line align-bottom me-1" />
                        Edit
                      </Link>
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "3" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("3");
                      }}
                      href="#"
                    >
                      Checklist
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="3">
              <ChecklistTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
